import React, { createContext, useContext, useEffect, useState } from 'react';

import { Bar, BarChart, CartesianGrid, LabelList, Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import ReportChartTooltip from '~/components/Common/ReportChartTooltip';

import { type Group, type Pair, type VendorData, metricColumns, n, pct, share, vendorChange } from './dashboard';
import './vendor-dashboard.css';

const DataContext = createContext<VendorData | null>(null);

type Props = {
  data: VendorData;
  native?: boolean;
  embedded?: boolean;
  currentOnly?: boolean;
  theme?: 'light' | 'dark';
  onMethod?: () => void;
  onExport?: (settings: Record<string, unknown>) => void;
  onPrint?: (settings: Record<string, unknown>) => void;
  onSettingsChange?: (settings: Record<string, unknown>) => void;
  scopeIdentity?: string;
  onContextChange?: (context: VendorContext) => void;
};
export type VendorContext = {
  identity: string;
  currency: string;
  refund: string;
  only: boolean;
  period: string;
  axisStart: number;
  axisDays: number;
  roomHighlight: string;
  statesOpen: boolean;
};

const short = (s: string, property: string) =>
  (s.startsWith(property) ? s.slice(property.length) : s).replace(/^\s*[–-]?\s*/, '');
const current = 'var(--fv-current)',
  comparison = 'var(--fv-comparison)';
const roomColour = (i: number) =>
  ['var(--fv-room-1)', 'var(--fv-room-2)', 'var(--fv-room-3)', 'var(--fv-room-4)', 'var(--fv-room-5)'][i % 5];
const dash = ['', '7 3', '2 3', '10 3 2 3', '14 4', '3 2 1 2', '8 2 2 2', '1 3', '12 2 2 2 2 2'];
function Source({ children }: { children?: React.ReactNode }) {
  const d = useContext(DataContext);
  return <p className="fv-source">{children ?? `Campaign sales · through ${d.asOf}`}</p>;
}
// Samples are SVG because the PDF engine keeps an SVG's size but not a CSS-sized empty box.
function Legend({ only }: { only: boolean }) {
  return (
    <div className="fv-legend">
      <span>
        <svg width="14" height="7" aria-hidden="true">
          <rect width="14" height="7" fill={current} />
        </svg>
        Current offer
      </span>
      {!only && (
        <span>
          <svg width="14" height="7" aria-hidden="true">
            <rect width="14" height="5" fill={comparison} />
            <line x1="0" x2="14" y1="6" y2="6" stroke="var(--fv-muted)" strokeWidth="2" strokeDasharray="3 2" />
          </svg>
          Comparison offer
        </span>
      )}
    </div>
  );
}
function Panel({ title, className = '', children }: { title: string; className?: string; children: React.ReactNode }) {
  return (
    <section className={'fv-panel ' + className}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}
function PairBars({
  rows,
  only,
  measure,
  labels = false,
  wide = false,
}: {
  rows: Array<Pair>;
  only: boolean;
  measure: string;
  labels?: boolean;
  wide?: boolean;
}) {
  return (
    <div className="fv-chart-frame">
      <div className={'fv-chart fv-bars ' + (wide ? 'fv-wide-bars' : '')}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={rows}
            margin={{ top: labels ? 19 : 12, left: 0, right: 10, bottom: wide ? 35 : 14 }}
            barGap={1}
          >
            <CartesianGrid vertical={false} stroke="var(--fv-rule)" />
            <XAxis
              dataKey="name"
              interval={0}
              angle={wide ? -55 : 0}
              height={wide ? 48 : 28}
              textAnchor={wide ? 'end' : 'middle'}
              tick={{ fill: 'var(--fv-muted)', fontSize: wide ? 10 : 11 }}
            />
            <YAxis
              width={46}
              tick={{ fill: 'var(--fv-muted)', fontSize: 10 }}
              tickFormatter={(v) => n(v)}
              label={{ value: measure, angle: -90, position: 'insideLeft', fill: 'var(--fv-muted)', fontSize: 10 }}
            />
            <ReportChartTooltip
              contentStyle={{
                background: 'var(--fv-paper)',
                color: 'var(--fv-text)',
                border: '1px solid var(--fv-rule)',
              }}
              formatter={(v) => n(v as number)}
            />
            <Bar isAnimationActive={false} dataKey="current" name="Current offer" fill={current}>
              {labels && <LabelList dataKey="current" position="top" fontSize={10} fill="var(--fv-text)" />}
            </Bar>
            {!only && (
              <Bar
                isAnimationActive={false}
                dataKey="comparison"
                name="Comparison offer"
                fill={comparison}
                stroke="var(--fv-muted)"
                strokeWidth={0.4}
              >
                {labels && <LabelList dataKey="comparison" position="top" fontSize={10} fill="var(--fv-text)" />}
              </Bar>
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
function RoomTable({
  rows,
  total,
  label,
  selected,
  onSelect,
}: {
  rows: Array<Group>;
  total: number | null;
  label: string;
  selected: string;
  onSelect: (s: string) => void;
}) {
  const [sort, setSort] = useState('source');
  const list = [...rows];
  if (sort === 'packages') {
    list.sort((a, b) => (b.packages ?? -Infinity) - (a.packages ?? -Infinity));
  }
  if (sort === 'name') {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }
  return (
    <div className="fv-room-table">
      <h3>{label}</h3>
      <div className="fv-table-scroll">
        <table>
          <thead>
            <tr>
              <th>
                <button onClick={() => setSort(sort === 'name' ? 'source' : 'name')}>
                  Room type {sort === 'name' ? '↑' : '↕'}
                </button>
              </th>
              <th>
                {/* The PDF sheet lets th/td text break anywhere; fv-num spans keep words and numbers whole. */}
                <button onClick={() => setSort(sort === 'packages' ? 'source' : 'packages')}>
                  <span className="fv-num">Packages</span> {sort === 'packages' ? '↓' : '↕'}
                </button>
              </th>
              <th>
                <span className="fv-num">% sold</span>
              </th>
              <th>
                <span className="fv-num">ADR</span>
              </th>
              <th>
                <span className="fv-num">ALOS</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.name} data-selected={selected === r.name}>
                <td>
                  <button
                    title={r.name}
                    className="fv-room-name"
                    onClick={() => onSelect(selected === r.name ? '' : r.name)}
                  >
                    {r.name}
                  </button>
                </td>
                <td>
                  <span className="fv-num">{n(r.packages)}</span>
                </td>
                <td>
                  <span className="fv-num">{pct(share(r.packages, total), 1)}</span>
                </td>
                <td>
                  <span className="fv-num">{n(r.adr, 0)}</span>
                </td>
                <td>
                  <span className="fv-num">{n(r.alos, 1)}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
export default function VendorDashboard(props: Props) {
  return (
    <DataContext.Provider value={props.data}>
      <Dashboard {...props} />
    </DataContext.Provider>
  );
}
function Dashboard({
  data: d,
  native = true,
  embedded = false,
  currentOnly,
  theme = 'light',
  onMethod,
  onExport,
  onSettingsChange,
  onPrint,
  scopeIdentity = '',
  onContextChange,
}: Props) {
  const q = new URLSearchParams(location.search);
  let saved: Record<string, unknown> = {};
  try {
    saved = JSON.parse(document.getElementById('root')?.getAttribute('data-flash-settings') ?? '{}');
  } catch {}
  const [baseline, setBaseline] = useState(q.get('layout') === 'baseline');
  const [currency, setCurrency] = useState(() => {
    const value = String(saved.currency ?? q.get('fpcurrency') ?? q.get('currency') ?? '');
    return d.variants.some((variant) => variant.currency === value)
      ? value
      : (d.variants.find((variant) => variant.currency !== 'AUD')?.currency ?? 'AUD');
  });
  const [refund, setRefund] = useState(String(saved.refund ?? q.get('fprefund') ?? q.get('refund') ?? 'Before Refund'));
  const [only, setOnly] = useState(
    currentOnly ?? (saved.only === true || q.get('fpOnly') === '1' || q.get('currentOnly') === '1'),
  );
  const [period, setPeriod] = useState(String(saved.period ?? q.get('fpperiod') ?? q.get('period') ?? 'Full Period'));
  const [axisStart, setAxisStart] = useState(Number(saved.axisStart ?? q.get('fpaxisStart') ?? 0));
  const [axisDays, setAxisDays] = useState(Number(saved.axisDays ?? q.get('fpaxisDays') ?? q.get('axisDays') ?? 0));
  const [selected, setSelected] = useState('');
  const [method, setMethod] = useState(false);
  const [collapsed, setCollapsed] = useState<Array<string>>([]);
  const [sortCountry, setSortCountry] = useState(false);
  const [statesOpen, setStatesOpen] = useState(false);
  const v =
    d.variants.find((x) => x.currency === currency && x.refund === refund && x.period === period) ?? d.variants[0];
  const columns = [
    ...metricColumns,
    ...(v.hasInclusions ? [{ key: 'inclusions' as const, label: 'INCLUSION UPGRADES', digits: 2, percent: true }] : []),
  ];
  const settings = { currency, refund, only, period, axisDays, axisStart };
  useEffect(() => {
    onContextChange?.({
      identity: scopeIdentity,
      currency,
      refund,
      only,
      period,
      axisStart,
      axisDays,
      roomHighlight: selected,
      statesOpen,
    });
  }, [scopeIdentity, currency, refund, only, period, axisStart, axisDays, selected, statesOpen, onContextChange]);
  useEffect(() => {
    if (native) {
      if (!embedded) onSettingsChange?.(settings);
      return;
    }
    document.documentElement.dataset.theme = theme;
    // File downloads retain their selected scope in the root data attribute;
    // avoid browser-specific file-origin History API restrictions.
    if (location.protocol === 'file:') {
      return;
    }
    const p = new URLSearchParams(location.search);
    p.set('currency', currency);
    p.set('refund', refund);
    p.set('currentOnly', only ? '1' : '0');
    p.set('period', period);
    p.set('axisDays', String(axisDays));
    p.set('axisStart', String(axisStart));
    p.set('layout', baseline ? 'baseline' : 'standard');
    history.replaceState(null, '', '?' + p);
  }, [currency, refund, only, period, axisDays, axisStart, baseline, native, theme]);
  const nativeSearch = location.search;
  useEffect(() => {
    if (!native) {
      return;
    }
    const p = new URLSearchParams(nativeSearch);
    if (p.has('fpcurrency')) {
      setCurrency(p.get('fpcurrency'));
    }
    if (p.has('fprefund')) {
      setRefund(p.get('fprefund'));
    }
    if (p.has('fpaxisStart')) {
      setAxisStart(Number(p.get('fpaxisStart')) || 0);
    }
    if (p.has('fpaxisDays')) {
      setAxisDays(Number(p.get('fpaxisDays')) || 0);
    }
    if (p.has('fpperiod')) {
      setPeriod(p.get('fpperiod'));
    }
    if (currentOnly !== undefined) {
      setOnly(currentOnly);
    } else if (p.has('fpOnly')) {
      setOnly(p.get('fpOnly') === '1');
    }
  }, [native, nativeSearch, currentOnly]);
  const regionOrder = ['Australia', 'Asia', 'Europe', 'New Zealand', 'North America', 'India', 'Rest of the World'];
  const countries = [...v.countries].sort((a, b) =>
    sortCountry
      ? (b.current.packages ?? -Infinity) - (a.current.packages ?? -Infinity)
      : (!regionOrder.includes(a.region) ? 99 : regionOrder.indexOf(a.region)) -
          (!regionOrder.includes(b.region) ? 99 : regionOrder.indexOf(b.region)) || a.name.localeCompare(b.name),
  );
  const toggleRegion = (name: string) =>
    setCollapsed(collapsed.includes(name) ? collapsed.filter((v) => v !== name) : [...collapsed, name]);
  return (
    <article className={'flash-vendor ' + (baseline ? 'fv-baseline' : '')} data-theme={theme} data-native={native}>
      <header className="fv-header" style={embedded ? { display: 'none' } : undefined}>
        <div>
          <h1>Post Campaign | Vendor Report</h1>
          <p>
            <strong>Current offer: {d.current.name}</strong>{' '}
            <span>
              {d.current.from} to {v.to}
            </span>
          </p>
          {!only && (
            <p>
              Comparison: {d.comparison.name}{' '}
              <span>
                {d.comparison.from} to {v.cmpTo}
              </span>
            </p>
          )}
        </div>
        <div className="fv-actions">
          <button onClick={() => (onMethod ? onMethod() : setMethod(!method))}>Method & sources</button>
          <button onClick={() => (native ? onExport?.(settings) : onExport?.(settings))}>
            {native ? 'Export partner report' : 'Download HTML'}
          </button>
          <button onClick={() => (native ? onPrint?.(settings) : window.print())}>
            {native ? 'Open print view' : 'Print / PDF'}
          </button>
        </div>
      </header>
      <div className="fv-controls" style={embedded ? { display: 'none' } : undefined}>
        <label>
          Refund basis
          <select value={refund} onChange={(e) => setRefund(e.target.value)}>
            <option>Before Refund</option>
            <option>After Refund</option>
          </select>
        </label>
        <label>
          Currency
          <select value={currency} onChange={(e) => setCurrency(e.target.value)}>
            {[...new Set(d.variants.map((x) => x.currency))].map((x) => (
              <option key={x} value={x}>
                {x === 'AUD' ? 'AUD' : 'Vendor currency · ' + x}
              </option>
            ))}
          </select>
        </label>
        <label>
          Comp. period
          <select disabled={only} value={period} onChange={(e) => setPeriod(e.target.value)}>
            {['Full Period', 'First 30 Days', 'First 60 Days', 'First 90 Days'].map((x) => (
              <option key={x}>{x}</option>
            ))}
          </select>
        </label>
        <label>
          Chart start day
          <input
            min="0"
            type="number"
            step="1"
            value={axisStart}
            onChange={(e) => setAxisStart(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
          />
        </label>
        <label>
          Chart end day (0 = auto)
          <input
            type="number"
            min="0"
            step="1"
            value={axisDays}
            onChange={(e) => setAxisDays(Math.max(0, Math.trunc(Number(e.target.value) || 0)))}
          />
        </label>
        <label className="fv-check">
          <input type="checkbox" checked={only} onChange={(e) => setOnly(e.target.checked)} />
          Current offer only
        </label>
        <button
          className="fv-reset"
          onClick={() => {
            setCurrency(d.variants.find((x) => x.currency !== 'AUD')?.currency ?? 'AUD');
            setRefund('Before Refund');
            setPeriod('Full Period');
            setAxisDays(0);
            setAxisStart(0);
            setOnly(false);
            setSelected('');
            setCollapsed([]);
            setSortCountry(false);
            setStatesOpen(false);
          }}
        >
          Reset filters
        </button>
        <span className="fv-snapshot">Data through {d.asOf}</span>
      </div>
      {v.currencyMismatch && (
        <p role="alert">Current and comparison vendor currencies differ. Select AUD to compare revenue and rates.</p>
      )}
      <div
        className="fv-score-scroll"
        tabIndex={0}
        role="region"
        aria-label="Performance comparison; scroll horizontally on narrow screens"
      >
        <table className="fv-score">
          <thead>
            <tr>
              <th scope="col">{refund}</th>
              {columns.map((m) => (
                <th key={m.key}>
                  {m.label}
                  {['revenue', 'adr'].includes(m.key) && <small>{currency}</small>}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(['current', ...(!only ? ['comparison'] : [])] as Array<'current' | 'comparison'>).map((side) => (
              <tr key={side}>
                <th scope="row">{side === 'current' ? 'Current' : 'Comparison'}</th>
                {columns.map((m) => (
                  <td key={m.key}>{m.percent ? pct(v[side][m.key], m.digits) : n(v[side][m.key], m.digits)}</td>
                ))}
              </tr>
            ))}
            {!only && (
              <tr className="fv-delta">
                <th scope="row">% Difference</th>
                {columns.map((m) => {
                  const delta =
                    m.key === 'inclusions'
                      ? v.inclusionDelta
                      : vendorChange(
                          m.key,
                          m.key === 'revenue' ? v.windowCurrentRevenue : v.current[m.key],
                          v.comparison[m.key],
                        );
                  return (
                    <td key={m.key} data-direction={delta !== null && delta < 0 ? 'negative' : 'positive'}>
                      {delta !== null && delta > 0 ? '+' : ''}
                      {m.percent ? (delta === null ? 'Unknown' : n(delta * 100, 1) + ' pp') : pct(delta)}
                    </td>
                  );
                })}
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="fv-grid">
        <Panel title="TOTAL CUMULATIVE ROOM NIGHTS SOLD" className="fv-total">
          <div className="fv-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={v.curve} margin={{ top: 12, right: 12, left: 0, bottom: 17 }}>
                <CartesianGrid vertical={false} stroke="var(--fv-rule)" />
                <XAxis
                  dataKey="day"
                  type="number"
                  domain={[axisStart, axisDays > 0 ? axisDays : 'dataMax']}
                  allowDataOverflow={axisDays > 0 || axisStart !== 0}
                  tick={{ fill: 'var(--fv-muted)', fontSize: 10 }}
                  label={{
                    value: 'Days since offer start',
                    position: 'bottom',
                    offset: 0,
                    fill: 'var(--fv-muted)',
                    fontSize: 11,
                  }}
                />
                <YAxis
                  width={48}
                  tickFormatter={(x) => n(x / 1000) + 'k'}
                  tick={{ fill: 'var(--fv-muted)', fontSize: 10 }}
                />
                <ReportChartTooltip
                  contentStyle={{ background: 'var(--fv-paper)', color: 'var(--fv-text)' }}
                  labelFormatter={(x) => 'Campaign day ' + x}
                  formatter={(x) => n(x as number)}
                />
                <Line
                  isAnimationActive={false}
                  dataKey="current"
                  name="Current offer"
                  stroke={current}
                  strokeWidth={2.5}
                  dot={false}
                />
                {!only && (
                  <Line
                    isAnimationActive={false}
                    dataKey="comparison"
                    name="Comparison offer"
                    stroke={comparison}
                    strokeDasharray="6 3"
                    strokeWidth={2.5}
                    dot={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <Legend only={only} />
          <Source />
        </Panel>
        <Panel title="ROOM TYPES" className="fv-rooms">
          <div className={'fv-room-pair ' + (only ? 'fv-one' : '')}>
            <RoomTable
              rows={v.rooms.current}
              total={v.current.packages}
              label={d.current.name}
              selected={selected}
              onSelect={setSelected}
            />
            {!only && (
              <RoomTable
                rows={v.rooms.comparison}
                total={v.comparison.packages}
                label={d.comparison.name}
                selected={selected}
                onSelect={setSelected}
              />
            )}
          </div>
          <Source>
            Packages, share, sale-side ADR ({currency}) and ALOS · click a room to highlight; column headers sort.
          </Source>
        </Panel>
        <div className="fv-middle-left">
          <Panel title="TOTAL CUMULATIVE ROOM NIGHTS SOLD BY ROOM TYPE" className="fv-room-curves">
            <div className="fv-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={v.roomCurve} margin={{ top: 8, right: 10, left: 0, bottom: 12 }}>
                  <CartesianGrid vertical={false} stroke="var(--fv-rule)" />
                  <XAxis
                    dataKey="day"
                    type="number"
                    domain={[axisStart, axisDays > 0 ? axisDays : 'dataMax']}
                    allowDataOverflow={axisDays > 0 || axisStart !== 0}
                    tick={{ fill: 'var(--fv-muted)', fontSize: 10 }}
                  />
                  <YAxis
                    width={48}
                    tickFormatter={(x) => n(x / 1000) + 'k'}
                    tick={{ fill: 'var(--fv-muted)', fontSize: 10 }}
                  />
                  <ReportChartTooltip
                    contentStyle={{ background: 'var(--fv-paper)', color: 'var(--fv-text)' }}
                    labelFormatter={(x) => 'Campaign day ' + x}
                    formatter={(x) => n(x as number)}
                  />
                  {v.roomNames.map((name, i) => (
                    <Line
                      isAnimationActive={false}
                      key={name}
                      dataKey={'r' + i}
                      name={name}
                      stroke={roomColour(i)}
                      strokeDasharray={dash[i % dash.length]}
                      strokeOpacity={selected && selected !== name ? 0.22 : 1}
                      strokeWidth={selected === name ? 3 : 1.8}
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="fv-room-legend" aria-label="Room highlights">
              {v.roomNames.map((name, i) => (
                <button
                  key={name}
                  data-lens-pdf-keep=""
                  aria-pressed={selected === name}
                  onClick={() => setSelected(selected === name ? '' : name)}
                  title={name}
                >
                  <svg width="24" height="8" aria-hidden="true">
                    <line
                      x1="0"
                      x2="24"
                      y1="4"
                      y2="4"
                      stroke={roomColour(i)}
                      strokeWidth="2"
                      strokeDasharray={dash[i % dash.length]}
                    />
                  </svg>
                  {name}
                </button>
              ))}
            </div>
            <Source>Current offer · days since offer start · select again to clear highlight.</Source>
          </Panel>
          <Panel
            title={only ? 'TOTAL BOOKINGS BY CHECK-IN DAY-OF-WEEK' : 'PACKAGES SOLD BY CHECK-IN DAY-OF-WEEK'}
            className="fv-weekday"
          >
            <PairBars
              rows={only ? v.dowBookings : v.dow}
              only={only}
              measure={only ? 'Bookings' : 'Packages sold'}
              labels
            />
            <Legend only={only} />
            <Source>Unknown = check-in date unavailable; may include Buy Now Book Later.</Source>
          </Panel>
        </div>
        <Panel title="CUSTOMER DETAILS" className="fv-customers">
          {only && (
            <button className="fv-state-toggle" aria-expanded={statesOpen} onClick={() => setStatesOpen(!statesOpen)}>
              {statesOpen ? 'Hide' : 'Show'} country / state breakdown
            </button>
          )}
          {only && statesOpen && (
            <div className="fv-table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Sub-region</th>
                    <th>Packages</th>
                    <th>% sold</th>
                    <th>Avg. lead days</th>
                  </tr>
                </thead>
                <tbody>
                  {[...v.states]
                    .sort((a, b) => a.country.localeCompare(b.country) || a.state.localeCompare(b.state))
                    .map((r) => (
                      <tr key={r.name}>
                        <td>{r.country}</td>
                        <td>{r.state}</td>
                        <td>{n(r.packages)}</td>
                        <td>{pct(share(r.packages, v.current.packages), 2)}</td>
                        <td>{n(r.lead)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ display: only && statesOpen ? 'none' : undefined }} className="fv-table-scroll">
            <table>
              <thead>
                <tr>
                  <th colSpan={2}>Customer geography</th>
                  <th colSpan={3}>Current offer · {short(d.current.name, d.property)}</th>
                  {!only && <th colSpan={3}>Comparison offer · {short(d.comparison.name, d.property)}</th>}
                </tr>
                <tr>
                  <th>Region</th>
                  <th>
                    <button onClick={() => setSortCountry(!sortCountry)}>
                      Country {sortCountry ? '↓ packages' : '↕'}
                    </button>
                  </th>
                  {(only ? [1] : [1, 2]).map((i) => (
                    <React.Fragment key={i}>
                      <th>Packages</th>
                      <th>% sold</th>
                      <th>
                        Avg. lead
                        <br />
                        days
                      </th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {countries.map((r, i) => {
                  const first = i === 0 || countries[i - 1].region !== r.region;
                  return (
                    <React.Fragment key={r.name}>
                      {first && collapsed.includes(r.region) && (
                        <tr>
                          <td colSpan={only ? 5 : 8}>
                            <button className="fv-region" aria-expanded={false} onClick={() => toggleRegion(r.region)}>
                              + {r.region}
                            </button>
                          </td>
                        </tr>
                      )}
                      {!collapsed.includes(r.region) && (
                        <tr>
                          <td>
                            {first && (
                              <button className="fv-region" aria-expanded onClick={() => toggleRegion(r.region)}>
                                − {r.region}
                              </button>
                            )}
                          </td>
                          <td>{r.name}</td>
                          {(['current', ...(!only ? ['comparison'] : [])] as Array<'current' | 'comparison'>).map(
                            (side) => (
                              <React.Fragment key={side}>
                                <td>{n(r[side].packages)}</td>
                                <td>{pct(share(r[side].packages, v[side].packages), 2)}</td>
                                <td>{n(r[side].lead)}</td>
                              </React.Fragment>
                            ),
                          )}
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Source>Aggregated country records only · click region to collapse/expand. No customer identities.</Source>
        </Panel>
        <div className="fv-bottom">
          <Panel title="PACKAGES SOLD BY CHECK-IN DATE · FROM FIRST CHECK-IN YEAR" className="fv-monthly">
            <PairBars rows={v.monthly} only={only} measure="Bookings (AR)" wide />
            <Legend only={only} />
            <Source>
              Saved worksheet measure: bookings after refunds, independent of refund basis. Y1/Y2 start at each offer’s
              first check-in year.
            </Source>
          </Panel>
          <Panel title="PACKAGES SOLD BY LEAD TIME" className="fv-lead">
            <PairBars rows={v.leadtime} only={only} measure="Bookings" wide />
            <Legend only={only} />
            <Source>
              {v.leadtime.some((x) => x.current !== null)
                ? 'Original worksheet measure: bookings · order date to check-in, in days · Unknown includes missing/negative dates.'
                : 'Unknown: verified order-date aggregate unavailable for this comparison period.'}
            </Source>
          </Panel>
        </div>
      </div>
      {!native && (
        <button className="fv-layout-toggle" onClick={() => setBaseline(!baseline)}>
          {baseline ? 'View source-based LE layout' : 'Compare conventional HTML'}
        </button>
      )}
      <footer className="fv-footer">
        {d.source} · transactions {d.current.from}–{v.to}
        {!only && ` / ${d.comparison.from}–${v.cmpTo}`} · Sydney dates · prepared {d.prepared}. Live warehouse evidence,
        selected-campaign edition; no automatic refresh. Revenue card: original item-level lifetime total; revenue %
        difference: campaign-window totals, as in Tableau.
      </footer>
      {method && (
        <div className="fv-method" role="dialog" aria-modal="true" aria-label="Method and sources">
          <div>
            <button className="fv-close" onClick={() => setMethod(false)}>
              Close
            </button>
            <h2>Method and sources</h2>
            <p>
              Reference: {d.workbook}, revision {d.revision}. Layout follows its eight-metric matrix, two room
              crosstabs, cumulative curves, customer table and grouped bars.
            </p>
            <p>
              This report contains aggregates for the selected campaigns and dates. Source transaction identifiers, PCM
              margin and membership information are excluded from partner exports.
            </p>
            <p>
              The current revenue card retains the original item-level FIXED calculation: cost less holdback across the
              live item history for items in the campaign scope. Comparison revenue is the selected-window sum. The
              original revenue % difference uses campaign-window sums on both sides; it therefore does not equal the
              percentage change between the displayed revenue cards. This distinction is preserved explicitly. All
              revenue values include refunds regardless of the refund control.
            </p>
            <p>
              Packages: distinct non-surcharge sale items less distinct refunded items on After Refund. Bookings:
              distinct sale orders less refunded orders. Room nights: signed source nights. ADR and ALOS use sale-side
              ratios. Average lead time uses transaction date to check-in, while lead-time buckets use original order
              date. Monthly bars retain the saved worksheet’s always-after-refund booking measure.
            </p>
            <p>
              Period caps and cumulative-chart day numbers use the source effective offer starts:{' '}
              {v.effectiveCurrentStart} current and {v.effectiveComparisonStart} comparison. Unknown values remain
              Unknown. Absent groups have no fabricated zeros. Refunds are those processed within each campaign window,
              not lifetime refunds. Selected campaign lengths are{' '}
              {Math.round((Date.parse(v.to) - Date.parse(d.current.from)) / 86400000) + 1} and{' '}
              {Math.round((Date.parse(v.cmpTo) - Date.parse(d.comparison.from)) / 86400000) + 1} days.
            </p>
            <p>
              Only vendor-facing aggregates are embedded. No internal margin, attribution, membership data, customer
              identities, raw item/order/offer IDs or source row payloads are exported.
            </p>
          </div>
        </div>
      )}
    </article>
  );
}
